import { Injectable } from '@angular/core';
import {
  APP_PRIVATE_PATHS,
  APP_TOP_LEVEL_PATH,
} from '@epicuro-next/navigation/constants';
import { ConfigurationsService } from '@epicuro-next/platform/configuration';
import { EnvironmentService } from '@epicuro-next/platform/environment';
import { selectLanguage } from '@epicuro-next/platform/i18n';
import { selectProduct } from '@epicuro-next/state/config-strip-state';
import {
  DabConfigurationKeys,
  DAB_FEATURE_NAME,
  loadDabHistory,
  loadDabHistoryFailure,
  loadDabHistorySuccess,
  selectDabHistory,
} from '@epicuro-next/state/dab-state';
import {
  selectSessionState,
  SITE_ID,
  SUB_SITE_ID,
} from '@epicuro-next/state/session-state';
import { toBoolean } from '@epicuro-next/utilities';
import { ComponentStore } from '@ngrx/component-store';
import { Actions, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import {
  DabHistory,
  DabHistoryType,
  Product,
} from 'graphql-api/gql-api-interfaces';
import { queueScheduler } from 'rxjs';
import { filter, mapTo, observeOn, pluck, takeUntil } from 'rxjs/operators';

interface IDabHistory extends DabHistory {
  orderLink: string;
}

interface State {
  history: IDabHistory[];
  loading: boolean;
}

@Injectable()
export class InboundDetailsComponentStore extends ComponentStore<State> {
  public readonly hasItems$ = this.select((state) => state.history.length > 0);
  public readonly loading$ = this.select((state) => state.loading);
  public readonly inboundHistory$ = this.select((state) =>
    state.history.filter((item) => item.type === DabHistoryType.Inbound),
  );
  public readonly deliveredHistory$ = this.select((state) =>
    state.history.filter((item) => item.type === DabHistoryType.Delivered),
  );
  public readonly locale$ = this.store.pipe(select(selectLanguage));

  constructor(
    private store: Store,
    private storeActions$: Actions,
    private configurationsService: ConfigurationsService,
    private environmentService: EnvironmentService,
  ) {
    super({
      loading: false,
      history: [],
    });

    this.configurationsService
      .getConfigValue({
        namespace: DAB_FEATURE_NAME,
        featureName: DabConfigurationKeys.InboundDetailsTurnedOn,
        defaultValue: false,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.subscribeProductKey();
        this.subscribeDabHistory();
        this.subscribeActions();
      });
  }

  private subscribeProductKey() {
    this.store
      .select(selectProduct)
      .pipe(
        filter((product) => !!product?.productId && !!product?.brandId),
        observeOn(queueScheduler),
        takeUntil(this.destroy$),
      )
      .subscribe((product: Product) =>
        this.store.dispatch(
          loadDabHistory({
            products: [
              { productId: product.productId, brandId: product.brandId },
            ],
          }),
        ),
      );
  }

  private subscribeDabHistory() {
    this.store
      .pipe(
        select(selectDabHistory),
        concatLatestFrom(() => [
          this.store.pipe(select(selectSessionState)),
          this.environmentService.environmentSettings.pipe(pluck('appDomain')),
          this.environmentService.environmentSettings.pipe(
            pluck('multiSiteSupport'),
          ),
        ]),
        observeOn(queueScheduler),
        takeUntil(this.destroy$),
      )
      .subscribe(([history, session, appDomain, multiSiteSupport]) => {
        const subSitePart =
          session && session[SUB_SITE_ID] ? ':' + session[SUB_SITE_ID] : '';
        const sitePart = toBoolean(multiSiteSupport)
          ? `/${APP_TOP_LEVEL_PATH.siteSelector.custom}/${session[SITE_ID]}${subSitePart}`
          : '';
        const host = `${appDomain}${sitePart}`;

        this.patchState({
          history: history.map((item) => {
            return {
              ...item,
              orderLink: `${host}/${APP_PRIVATE_PATHS.orderBook.path}/${item.order}`,
            };
          }),
        });
      });
  }

  private subscribeActions() {
    this.storeActions$
      .pipe(
        ofType(loadDabHistory),
        mapTo(true),
        observeOn(queueScheduler),
        takeUntil(this.destroy$),
      )
      .subscribe(this.patchLoading.bind(this));

    this.storeActions$
      .pipe(
        ofType(loadDabHistorySuccess, loadDabHistoryFailure),
        mapTo(false),
        observeOn(queueScheduler),
        takeUntil(this.destroy$),
      )
      .subscribe(this.patchLoading.bind(this));
  }

  private patchLoading(loading: boolean) {
    this.patchState({ loading });
  }
}
